/**
 * The Overview stage (plan docs/plans/lms-plan.md 7.2, 6.1, 6.2 rules 1 and
 * 11, 6.3; WP-40): the change summary first when this is a retrain or
 * acknowledge assignment, why it was assigned and when it is due, the
 * learning objectives (they open the course), the estimated minutes, what
 * the course does not cover, the clinical disclaimer, the sources (collapsed),
 * and Start (or Continue from the bookmark).
 */
import { format } from 'date-fns';
import { Clock, PlayCircle } from 'lucide-react';
import type { CourseDetail, MyAssignment } from '../../api/courses';
import type { CourseContent, CourseSource } from '../../content/types';
import { primaryBtnCls } from '../../../ui/styles';
import { headingCls, microLabelCls, noticeCls, panelCls, pillCls, stageCls } from './styles';

export interface OverviewStageProps {
  course: CourseDetail;
  content: CourseContent;
  assignment: MyAssignment | null;
  versionNo: number;
  changeSummary: string | null;
  sources: CourseSource[];
  hasStarted: boolean;
  onStart: () => void;
}

const DUE_TONES: Record<NonNullable<MyAssignment['due_state']>, { tone: 'red' | 'amber' | 'primary'; word: string }> = {
  overdue: { tone: 'red', word: 'Overdue' },
  due_soon: { tone: 'amber', word: 'Due soon' },
  upcoming: { tone: 'primary', word: 'Assigned' },
};

function dueLine(assignment: MyAssignment): string {
  const d = new Date(assignment.due_at);
  return Number.isNaN(d.getTime()) ? '' : `Due ${format(d, 'd MMM yyyy')}`;
}

export function OverviewStage({ course, content, assignment, versionNo, changeSummary, sources, hasStarted, onStart }: OverviewStageProps) {
  const objectives = Array.isArray(content.learning_objectives) && content.learning_objectives.length > 0
    ? content.learning_objectives
    : course.objectives;
  const minutes = course.current_version?.estimated_minutes ?? null;
  const due = assignment?.due_state ? DUE_TONES[assignment.due_state] : null;
  const notCovered = content.not_covered || course.not_covered;
  const disclaimer = content.disclaimer || course.disclaimer;

  return (
    <div className={`${stageCls} space-y-4`} data-testid="lms-overview-stage">
      {changeSummary && (
        <div className={`${panelCls} space-y-2`} data-testid="lms-overview-change-summary">
          <div className={microLabelCls}>{`What changed in version ${versionNo}`}</div>
          <p className="whitespace-pre-line text-sm text-gray-800 dark:text-gray-200">{changeSummary}</p>
        </div>
      )}

      <div className={`${panelCls} space-y-4`}>
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <span>{`Version ${versionNo}`}</span>
          {typeof minutes === 'number' && (
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" aria-hidden="true" />
              {`About ${minutes} minutes`}
            </span>
          )}
          {assignment && due && <span className={pillCls(due.tone)}>{due.word}</span>}
          {assignment && <span>{dueLine(assignment)}</span>}
        </div>
        {assignment?.source_label && (
          <p className="text-sm text-gray-600 dark:text-gray-300" data-testid="lms-overview-reason">
            {assignment.source_label}
          </p>
        )}
        {course.description && <p className="text-sm text-gray-700 dark:text-gray-300">{course.description}</p>}

        {objectives.length > 0 && (
          <div>
            <h2 className={headingCls}>What you will be able to do</h2>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700 dark:text-gray-300" data-testid="lms-overview-objectives">
              {objectives.map((o, i) => (
                <li key={i}>{o}</li>
              ))}
            </ul>
          </div>
        )}

        {disclaimer && <div className={noticeCls('amber')}>{disclaimer}</div>}
        {notCovered && (
          <p className="text-sm text-gray-600 dark:text-gray-300">
            <span className="font-semibold text-gray-900 dark:text-gray-100">Not covered here: </span>
            {notCovered}
          </p>
        )}

        {sources.length > 0 && (
          <details className="rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700" data-testid="lms-overview-sources">
            <summary className="flex min-h-[44px] cursor-pointer items-center text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-200 sm:min-h-0">
              {`Sources (${sources.length})`}
            </summary>
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs text-gray-600 dark:text-gray-300">
              {sources.map((s, i) => (
                <li key={i}>
                  {`${s.title}${s.publisher ? `, ${s.publisher}` : ''}`}
                  {s.url ? (
                    <>
                      {'. '}
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="break-all text-primary-600 underline underline-offset-2 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400"
                      >
                        {s.url}
                      </a>
                    </>
                  ) : null}
                  {s.accessed_on ? ` (accessed ${s.accessed_on})` : ''}
                </li>
              ))}
            </ol>
          </details>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button type="button" className={`${primaryBtnCls} sm:px-4 sm:py-2`} onClick={onStart} data-testid="lms-overview-start">
            <PlayCircle className="h-4 w-4" aria-hidden="true" />
            {hasStarted ? 'Continue' : 'Start'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default OverviewStage;
